/**
 * Content-driven CV document direction.
 *
 * The CV document renders in ITS OWN language, never the app chrome's: an
 * English CV inside the Arabic app stays fully English/LTR (labels included),
 * and an Arabic CV inside the English app stays Arabic/RTL. This mirrors what
 * the API exports already do (`detectDocumentLocale`, issues #79/#80) and is
 * shared by the DOM renderer and both native previews so every surface makes
 * the same call.
 *
 * Only narrative fields count — contact rows, URLs, skills, and proper nouns
 * routinely stay Latin inside genuinely Arabic CVs.
 */

const ARABIC_LETTER_RE =
  /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]/g;
const LATIN_LETTER_RE = /[A-Za-z]/g;

export interface CvContentDirectionInput {
  headline?: string | null;
  summary?: string | null;
  highlights?: Array<{
    title?: string | null;
    desc?: string | null;
  }> | null;
  experience?: Array<{
    role?: string | null;
    overview?: string | null;
    bullets?: string[] | null;
  }> | null;
  education?: Array<{ description?: string | null }> | null;
  projects?: Array<{ description?: string | null }> | null;
}

/**
 * Returns 'rtl' when the narrative content is majority-Arabic, 'ltr' when it
 * is majority-Latin, and null when there is no detectable signal (empty CV) —
 * callers break the tie with the app locale so a blank manual CV still opens
 * in the user's language.
 */
export function detectCvContentDirection(
  profile: CvContentDirectionInput,
): 'rtl' | 'ltr' | null {
  let arabic = 0;
  let latin = 0;
  const add = (value?: string | null) => {
    if (!value) return;
    arabic += value.match(ARABIC_LETTER_RE)?.length ?? 0;
    latin += value.match(LATIN_LETTER_RE)?.length ?? 0;
  };
  add(profile.headline);
  add(profile.summary);
  for (const highlight of profile.highlights ?? []) {
    add(highlight?.title);
    add(highlight?.desc);
  }
  for (const experience of profile.experience ?? []) {
    add(experience?.role);
    add(experience?.overview);
    for (const bullet of experience?.bullets ?? []) add(bullet);
  }
  for (const education of profile.education ?? []) {
    add(education?.description);
  }
  for (const project of profile.projects ?? []) {
    add(project?.description);
  }
  const total = arabic + latin;
  if (total === 0) return null;
  return arabic / total > 0.5 ? 'rtl' : 'ltr';
}

/** True when the value contains at least one Arabic-script letter. */
export function containsArabicScript(value?: string | null): boolean {
  if (!value) return false;
  return (value.match(ARABIC_LETTER_RE)?.length ?? 0) > 0;
}
